/**
 * @Description This is fasatSecuritySettings container page
 * @FileName fasatSecuritySettings.js
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 27 January, 2021 11:15:07
 * @IssueID CDTFASATMOD-502
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../../index.scss';
import './fasatHTMLgrid.scss';
import PropTypes, { string } from 'prop-types';
import FasatCheckbox from '../fasatCheckbox/fasatCheckbox';
import { NoData } from '../../libs/fasatSharedComponent/customGrid/styledComponents';
import { staticCommonLabelKeys } from '../../moduleConstants';
import { currencyFormat } from '../../util';
import { getCurrentLocale } from '../../i18n';

/* global Set */ // eslint-disable-line no-redeclare

export const getAmount = (amt) => {
  const amtVal = amt || '0.00 ';
  const decimalPlace = '2';
  const localeGet = getCurrentLocale();
  return currencyFormat(amtVal.toString(), localeGet, decimalPlace, 'withSymbol');
};

export const getPercent = (amt) => {
  const percentVal = '2';
  return amt !== null ? `${amt.toFixed(percentVal)}%` : '';
};

const theadRow = ({ header, matTable, selectAll, headerChkboxAreaText, isAllSelected }) => (
  <thead>
    {matTable ? (
      <tr>
        <th>
          <FasatCheckbox id="selectAllChkBox" className="selection-all" aria-label={headerChkboxAreaText} onHandleChange={selectAll} value={isAllSelected} />
        </th>

        {header.map((th) => (
          <th key={th}>{th}</th>
        ))}
      </tr>
    ) : (
      <tr className="activity">
        {header.map((th) => (
          <th key={th}>{th}</th>
        ))}
      </tr>
    )}
  </thead>
);

export const getValue = (compVal, showData) => {
  let dataVal;
  if (compVal === 'DR') {
    dataVal = getAmount(showData);
  } else {
    dataVal = getPercent(showData);
  }
  return dataVal;
};

const getTables = ({ data, matTable, negativeRateData, jobStatusData, rowSelection, selectedKeys }) => {
  let val;
  if (matTable) {
    val = data.map((tr, key) => (
      <tr key={tr.key}>
        <td>
          <FasatCheckbox
            className="selection-box"
            id={tr.key}
            name={`matCheckbox-${key}`}
            onHandleChange={rowSelection(tr)}
            title={`matCheckbox-${key}`}
            value={selectedKeys.has(`${tr.orgCode}-${tr.orgLevel}`)}
          />
        </td>
        <td>{tr.level}</td>
        <td>{tr.type}</td>
        <td>{tr.orgCode}</td>
        <td>{tr.name}</td>
      </tr>
    ));
  }
  if (negativeRateData) {
    val = data.map((tr) => (
      <tr key={tr.key}>
        <td>{tr.agentCode}</td>
        <td>{tr.rateMasterId}</td>
        <td>{tr.rateDetailId}</td>
        <td>
          {getValue(tr.rateBasis, tr.premiumRate)}

        </td>
        <td>{tr.rateBasis}</td>
        <td>{tr.rateDuration}</td>
        <td>{tr.rateTerm}</td>
        <td>{tr.rateMethod}</td>
        <td>
          {getValue(tr.rateBasis, tr.chargeBackRate)}

        </td>
        <td>{tr.chargeBackRateBasis}</td>
        <td>{tr.chargeBackRateMethod}</td>

      </tr>
    ));
  }
  if (jobStatusData) {
    val = data.map((tr) => (
      <tr key={tr.key}>
        <td>{tr}</td>
      </tr>
    ));
  }
  return val;
};

const tbodyRow = ({ data, matTable, header, t, negativeRateData, jobStatusData, rowSelection, selectedKeys }) => (
  <tbody>
    {data.length ? (
      getTables({ data, matTable, negativeRateData, jobStatusData, rowSelection, selectedKeys })) : (
        <tr>
          <td colSpan={header.length + 1} className="withNoRow">
            <NoData>{t(staticCommonLabelKeys.COMMON_MSG_NO_DATA_AVAILABLE)}</NoData>
          </td>
        </tr>
    )}
  </tbody>
);

// Helper: Generate item key for selection tracking
const getItemKey = (item) => `${item.orgCode}-${item.orgLevel}`;

// Helper: Handle select all action
const handleSelectAll = (status, data, selectedValueRef, setSelectedKeys, getSelectedRows) => {
  if (status) {
    const allKeys = new Set(data.map(getItemKey));
    setSelectedKeys(allKeys);
    selectedValueRef.current = data.map((item) => ({ original: item }));
  } else {
    setSelectedKeys(new Set());
    selectedValueRef.current = [];
  }
  getSelectedRows(selectedValueRef.current);
};

// Helper: Handle single row selection
const handleRowSelection = (status, value, selectedKeys, selectedValueRef, setSelectedKeys, getSelectedRows) => {
  let prevSelection = selectedValueRef.current || [];
  const newSelectedKeys = new Set(selectedKeys);
  const itemKey = getItemKey(value);

  if (status) {
    prevSelection.push({ original: value });
    newSelectedKeys.add(itemKey);
  } else {
    prevSelection = prevSelection.filter(
      (item) => !(item.original.orgCode === value.orgCode && item.original.orgLevel === value.orgLevel),
    );
    newSelectedKeys.delete(itemKey);
  }
  selectedValueRef.current = prevSelection;
  setSelectedKeys(newSelectedKeys);
  getSelectedRows(selectedValueRef.current);
};

// Helper: Reset selection state
const resetSelection = (selectedValueRef, setSelectedKeys, getSelectedRows) => {
  setSelectedKeys(new Set());
  selectedValueRef.current = [];
  getSelectedRows(selectedValueRef.current);
};

function FasatHTMLgrid(props) {
  const [t] = useTranslation();
  const { data, header, selectedValueRef, getSelectedRows, tableCaption, checkBoxFlag, headerChkboxAreaText,
    negativeRateData, jobStatusData } = props;
  const matTable = checkBoxFlag;
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  const selectAll = (status) => handleSelectAll(status, data, selectedValueRef, setSelectedKeys, getSelectedRows);
  const rowSelection = (status, value) => handleRowSelection(status, value, selectedKeys, selectedValueRef, setSelectedKeys, getSelectedRows);
  const isAllSelected = data.length > 0 && selectedKeys.size === data.length;

  useEffect(() => { resetSelection(selectedValueRef, setSelectedKeys, getSelectedRows); }, [data]);

  const handleCheckBoxChange = useCallback((tr) => (status) => { rowSelection(status, tr); }, [rowSelection, selectedKeys]);

  return (
    <table className="fasatHTMLgrid" data-testid="TableComponent" cellPadding="0" cellSpacing="0">
      <caption>{tableCaption}</caption>
      {theadRow({ header, matTable, selectAll, headerChkboxAreaText, isAllSelected })}
      {tbodyRow({ data, matTable, header, t, negativeRateData, jobStatusData, selectedKeys, rowSelection: handleCheckBoxChange })}
    </table>
  );
}

FasatHTMLgrid.propTypes = {
  header: PropTypes.shape(),
  data: PropTypes.shape(),
  checkBoxFlag: PropTypes.bool,
  selectedValueRef: PropTypes.shape(),
  getSelectedRows: PropTypes.shape(),
  tableCaption: string,
  headerChkboxAreaText: string,
  negativeRateData: PropTypes.bool,
  jobStatusData: PropTypes.bool,
};

FasatHTMLgrid.defaultProps = {
  header: [],
  data: [],
  checkBoxFlag: true,
  selectedValueRef: {},
  getSelectedRows: () => null,
  tableCaption: '',
  headerChkboxAreaText: '',
  negativeRateData: false,
  jobStatusData: false,
};

export default FasatHTMLgrid;
